import {
  CadClickInstance,
  CadClickInstanceId,
  CadClickModel,
  Viewer3DInstance,
  Viewer3DOptions,
  Viewer3DType,
} from "@plansoft-configuration-renderer/components";

// cc3 model and parameter used to load each part's CAD file. The viewer and
// the STEP download load their parts from different folders.
export interface CadProductSource {
  model: string;
  question: string;
}

export const VIEWER_CAD_PRODUCT_SOURCE: CadProductSource = {
  model: "STP/STEP_NAME.cc3",
  question: "STEP_NAME",
};

export const STEP_DOWNLOAD_CAD_PRODUCT_SOURCE: CadProductSource = {
  model: "STP_MS/STEP_NAME.cc3",
  question: "STEP_NAME",
};

export const EMPTY_CAD_CLICK_MODEL: CadClickModel = {
  assembly: { instances: [] },
};

// swapAxis is a real Viewer3D webApi runtime option, but isn't modeled in the library's typed Viewer3DOptions["services"]["webApi"]
export type WebApiOptionsWithSwapAxis = NonNullable<
  NonNullable<Viewer3DOptions["services"]>["webApi"]
> & { swapAxis?: boolean };

interface CadPlacementConnector {
  id: string;
  rotation: { x: number; y: number; z: number };
  position: { x: number; y: number; z: number };
}

interface CadPlacementSourceConnector {
  id: string;
  part_id: string;
}

interface CadPlacementItem {
  id: string;
  connectors: CadPlacementConnector[];
  fileName: string;
  targetConnector: string;
  status: string;
  sourceConnector: "ROOT" | CadPlacementSourceConnector;
  visible: boolean;
}

interface CadClickAnchorPoint {
  name: string;
  pos_x: number;
  pos_y: number;
  pos_z: number;
  id: string;
  xaxis_x: number;
  xaxis_y: number;
  xaxis_z: number;
  yaxis_x: number;
  yaxis_y: number;
  yaxis_z: number;
  zaxis_x: number;
  zaxis_y: number;
  zaxis_z: number;
  type: "generated";
}

interface CadClickStatusColor {
  r: number;
  g: number;
  b: number;
  alpha: number;
}

interface CadClickAppearance {
  color: Partial<
    Record<
      "highlighted" | "active" | "hidden" | "hidden2" | "visible" | "orig",
      CadClickStatusColor
    >
  >;
  status?:
    | "highlighted"
    | "active"
    | "hidden"
    | "hidden2"
    | "visible"
    | "orig"
    | string;
  visible: boolean;
}

// the runtime CAD viewer accepts `anchors` and `appearance` on each instance, but
// the library's public CadClickInstance type doesn't declare either of them
type CadClickInstanceWithAnchors = CadClickInstance & {
  anchors: CadClickAnchorPoint[];
  appearance?: CadClickAppearance;
};

type ThreeVector3 = { x: number; y: number; z: number };

interface ThreeNamespace {
  Euler: new (x: number, y: number, z: number, order: string) => unknown;
  MathUtils: { degToRad: (deg: number) => number };
  Matrix4: new () => { makeRotationFromEuler: (euler: unknown) => unknown };
  Vector3: new () => {
    setFromMatrixColumn: (matrix: unknown, index: number) => ThreeVector3;
  };
}

declare global {
  interface Window {
    Viewer3D?: { THREE: ThreeNamespace };
  }
}

function waitForViewer3DThree(): Promise<ThreeNamespace> {
  return new Promise((resolve) => {
    const existing = window.Viewer3D?.THREE;
    if (existing) {
      resolve(existing);
      return;
    }

    const intervalId = window.setInterval(() => {
      const three = window.Viewer3D?.THREE;
      if (three) {
        window.clearInterval(intervalId);
        resolve(three);
      }
    }, 50);
  });
}

interface AxisComponents {
  xaxis_x: number;
  xaxis_y: number;
  xaxis_z: number;
  yaxis_x: number;
  yaxis_y: number;
  yaxis_z: number;
  zaxis_x: number;
  zaxis_y: number;
  zaxis_z: number;
}

function rotationToAxes(
  rotation: { x: number; y: number; z: number },
  three: ThreeNamespace,
): AxisComponents {
  const euler = new three.Euler(
    three.MathUtils.degToRad(rotation.x),
    three.MathUtils.degToRad(rotation.y),
    three.MathUtils.degToRad(rotation.z),
    "XYZ",
  );

  const matrix = new three.Matrix4().makeRotationFromEuler(euler);

  const xAxis = new three.Vector3().setFromMatrixColumn(matrix, 0);
  const yAxis = new three.Vector3().setFromMatrixColumn(matrix, 1);
  const zAxis = new three.Vector3().setFromMatrixColumn(matrix, 2);

  const clean = (v: number): number =>
    Math.abs(v) < 1e-10 ? 0 : Number(v.toFixed(10));

  return {
    xaxis_x: clean(xAxis.x),
    xaxis_y: clean(xAxis.y),
    xaxis_z: clean(xAxis.z),
    yaxis_x: clean(yAxis.x),
    yaxis_y: clean(yAxis.y),
    yaxis_z: clean(yAxis.z),
    zaxis_x: clean(zAxis.x),
    zaxis_y: clean(zAxis.y),
    zaxis_z: clean(zAxis.z),
  };
}

function toNumericId(index: number): CadClickInstanceId {
  return String(index) as CadClickInstanceId;
}

function getInstanceIdByName(
  name: string,
  instances: CadClickInstanceWithAnchors[],
): CadClickInstanceId | null {
  const instance = instances.find((inst) => inst.name === name);
  return instance ? instance.id : null;
}

/**
 * Builds a CadClickModel from a list of CadPlacementItems, mirroring the instance
 * and anchor construction from the standalone main.js CadClick example.
 */
async function buildCadClickModelFromItems(
  items: CadPlacementItem[],
  localeCode: string,
  productSource: CadProductSource,
): Promise<CadClickModel> {
  const three = await waitForViewer3DThree();

  const instances: CadClickInstanceWithAnchors[] = [];

  items.forEach((item, index) => {
    const instance: CadClickInstanceWithAnchors = {
      id: toNumericId(index),
      name: item.id,
      product: {
        library: null,
        model: productSource.model,
        parametersListOrder: false,
        cultureId: localeCode,
        booleanOp: "None",
        parameters: [
          { question: productSource.question, value: item.fileName },
        ],
      },
      placement: {
        source: null,
        target: null,
        transformations: [],
      },
      properties: {
        receiveClickEvent: true,
        showAnchor: false,
        anchorsToSensitivObjects: true,
        anchorsToSensitiveObjects: true,
      },
      custom: {},
      anchors: [],
      appearance: {
        color: {
          highlighted: { r: 0, g: 148, b: 156, alpha: 1 },
          active: { r: 0, g: 148, b: 156, alpha: 1 },
          hidden: { r: 100, g: 100, b: 100, alpha: 0.25 },
          hidden2: { r: 100, g: 100, b: 100, alpha: 0.75 },
        },
        status: item.status,
        visible: item.visible,
      },
    };

    if (item.sourceConnector !== "ROOT") {
      instance.placement = {
        source: { anchor: item.targetConnector },
        target: {
          anchor: item.sourceConnector.id,
          id: getInstanceIdByName(item.sourceConnector.part_id, instances),
        },
        transformations: [],
      };
    }

    instance.anchors = item.connectors.map((connector) => {
      const axes = rotationToAxes(connector.rotation, three);

      return {
        name: connector.id,
        pos_x: connector.position.x,
        pos_y: connector.position.y,
        pos_z: connector.position.z,
        id: instance.id,
        type: "generated",
        ...axes,
      };
    });

    instances.push(instance);
  });

  return {
    assembly: {
      instances: instances.map((instance) => ({ instance })),
    },
  };
}

/**
 * Builds a CadClickModel from the raw output of the "getCadJsonForPreview"
 * user defined getter. Despite its name, this getter returns the real,
 * current placement data for the configuration - it's called on every
 * configuration change, not just once for an initial mock preview.
 */
export async function buildCadClickModelFromGetterOutput(
  output: string,
  localeCode: string,
  productSource: CadProductSource = VIEWER_CAD_PRODUCT_SOURCE,
): Promise<CadClickModel> {
  const items: CadPlacementItem[] = JSON.parse(output);

  return buildCadClickModelFromItems(items, localeCode, productSource);
}

// the runtime CAD viewer assembly exposes `instances` and accepts export options,
// but the library's public Viewer3DAssembly type doesn't declare either of them
export interface CadClickRuntimeAssembly {
  instances: Viewer3DInstance[];
  exportJSON: (options: {
    ccWebApi: boolean;
    format: "absolute" | "relative";
  }) => CadClickModel;
}

interface StepExportCreateSettings {
  files: Array<{
    format: string;
    name: string;
    settings: Array<{ name: string; value: string }>;
  }>;
  returnStreams: boolean;
  zipAllFiles: boolean;
}

export type StepExportBuildInstruction = CadClickModel & {
  create: StepExportCreateSettings;
};

export function getCreate(): StepExportCreateSettings {
  return {
    files: [
      {
        format: "STEP",
        name: "export",
        settings: [
          {
            name: "STEP_SCHEME",
            value: "AP 214",
          },
          {
            name: "OPTIMIZED_FOR_UGS",
            value: "false",
          },
          {
            name: "OPTIMIZE_FILE_SIZE",
            value: "false",
          },
          {
            name: "CREATE_ACIS_ASSEMBLY_STRUCTURE",
            value: "true",
          },
          {
            name: "CREATE_INSTANCES",
            value: "false",
          },
        ],
      },
    ],
    returnStreams: false,
    zipAllFiles: false,
  };
}

/**
 * Builds the assembly/generate build instruction for the STEP download from
 * the instances currently loaded in the viewer. The same JSON is stored with
 * the cart position so the cart can regenerate the download later.
 */
export function buildStepExportInstruction(
  viewer3D: Viewer3DType,
): StepExportBuildInstruction {
  const assembly =
    new viewer3D.Assembly() as unknown as CadClickRuntimeAssembly;

  assembly.instances = viewer3D.getAllInstances();

  const model = assembly.exportJSON({
    ccWebApi: true,
    format: "absolute",
  });

  // the STEP download loads its parts from STP_MS instead of STP. Swap the
  // model on the exported JSON, not on the live viewer instances, so the
  // viewer itself is left untouched
  model.assembly.instances.forEach(({ instance }) => {
    instance.product.model = STEP_DOWNLOAD_CAD_PRODUCT_SOURCE.model;
  });

  return {
    ...model,
    create: getCreate(),
  };
}

export interface CadClickWebApiConfig {
  url?: string;
  apiKey?: string;
}

/**
 * Sends a build instruction to the CADClick webApi and returns the URLs of the
 * generated files.
 */
export async function generateStepExport(
  buildInstruction: StepExportBuildInstruction,
  webApi: CadClickWebApiConfig,
): Promise<string[]> {
  const response = await fetch(`${webApi.url}/assembly/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": webApi.apiKey ?? "",
    },
    body: JSON.stringify(buildInstruction),
  });

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  // the webApi answers with the generated file URL(s), either as a single
  // string or as a list with one URL per created file
  const result: unknown = await response.json();
  const fileUrls = Array.isArray(result) ? result : [result];

  if (
    fileUrls.length === 0 ||
    !fileUrls.every((fileUrl) => typeof fileUrl === "string")
  ) {
    throw new Error(
      "Unexpected assembly/generate response: " + JSON.stringify(result),
    );
  }

  return fileUrls as string[];
}
